import { TableProps } from "@mantine/core";
import { usePagination } from "@mantine/hooks";
import React from "react";

import { colors } from "#app/theme/v1/colors";
import { fontWeight } from "#app/theme/v1/fontWeights";
import { Pagination } from "#shared/view/components/Pagination";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Table } from "#shared/view/designSystem/Table";

type Props = {
  headers?: { id?: string; label?: string }[];
  elements?: Record<string, React.ReactNode>[];
  highlightLastRow?: boolean;
  modal?: React.ReactNode;
  paginated?: boolean;
  elementsPerPage?: number;
} & TableProps;

export const BasicTable: React.FC<Props> = ({
  headers,
  elements,
  highlightLastRow,
  modal,
  paginated,
  elementsPerPage = 10,
  ...props
}) => {
  const numberOfPages = Math.ceil((elements?.length || 0) / elementsPerPage);
  const { active, setPage } = usePagination({
    initialPage: 1,
    total: numberOfPages,
  });

  const headerRows = headers?.map(({ id, label }, index) => (
    <Table.Th key={id || label || index}>
      <Text style={headerStyles} fz={"sm"} p="sm">
        {label || ""}
      </Text>
    </Table.Th>
  ));

  const start = (active - 1) * elementsPerPage;
  const end = start + elementsPerPage;
  const visibleElements = paginated ? elements?.slice(start, end) : elements;

  const rows = visibleElements?.map((element, rowIndex) => {
    const cells = Object.values(element).map((cell, cellIndex) => {
      const isLastRow = rowIndex === visibleElements.length - 1;

      return (
        <Table.Td key={cellIndex}>
          <Text
            style={cellStyles}
            fz={"md"}
            p="sm"
            fw={highlightLastRow && isLastRow ? fontWeight.bold : fontWeight.regular}
          >
            {cell}
          </Text>
        </Table.Td>
      );
    });

    return <Table.Tr key={rowIndex}>{cells}</Table.Tr>;
  });

  return (
    <Stack maw="100%">
      <Table.ScrollContainer minWidth={100}>
        <Table {...props} style={tableStyles}>
          <Table.Thead>
            <Table.Tr>{headerRows}</Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {paginated && (
        <Pagination
          total={numberOfPages}
          value={active}
          onNextPage={() => setPage(active + 1)}
          onPreviousPage={() => setPage(active - 1)}
          onChange={(page) => setPage(page)}
        />
      )}
      {modal}
    </Stack>
  );
};

const tableStyles = {
  border: `1px solid ${colors.gray[3]}`,
};

const headerStyles = {
  color: "black",
  fontWeight: fontWeight.bold,
  display: "flex",
  justifyContent: "center",
};

const cellStyles = {
  display: "flex",
  justifyContent: "center",
};
